import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { UserService } from '../../../service/user.service';
import { InvestmentDialogComponent } from '../../client/investment-dialog/investment-dialog.component';
import { HttpClient } from '@angular/common/http';
import { PortfolioService } from '../../../service/portfolio.service';
import { TokenIdService } from '../../../service/token-id.service';
import { PrimengImports } from '../../../shared/primeng-imports';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ImageUrlPipe } from '../../../pipes/image-url.pipe';

interface Portfolio {
  maturityDate: string | null;
  _id: string;
  name: string;
  riskLevel: string;
  description: string;
  roiPotential: number;
  tenure: number;
  investmentFocus: string[];
  investmentStartDate: string;
  investmentClosureDate: string;
  status: string;
  isActive: boolean;
  isDeleted: boolean;
  isPublished: boolean;
  ticketPrice: number;
  totalTickets: number;
  availableTickets: number;
  soldTickets: number;
  images: string[];
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface UserInvestment {
  _id: string;
  userId: string;
  portfolioId: Portfolio;
  totalInvestedAmount: number;
  totalTickets: number;
  investmentCount: number;
  currentValue: number;
  actualRoiPercentage: number;
  roiAmount: number;
  roiEarnedTotal: number;
  roiAvailableFromThisPortfolio: number;
  profitLoss: number;
  investmentStartDate: string;
  lastInvestmentDate: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  isPublished:boolean;
  __v: number;
}

interface UserInvestmentResponse {
  success: boolean;
  data: UserInvestment[];
}

@Component({
  selector: 'app-portfolios-tab',
  standalone: true,
  imports: [CommonModule, LucideIconsModule, InvestmentDialogComponent, PrimengImports, ImageUrlPipe],
  templateUrl: './portfolios-tab.component.html',
  styleUrl: './portfolios-tab.component.css'
})
export class PortfoliosTabComponent implements OnInit {
  userInvestments: UserInvestment[] = [];
  isLoading: boolean = false;
  error: string | null = null;
  userId: string = ''; // This should be set from authenticated user
  isAuthenticated: boolean = false;

  portfolioIcons: { [key: string]: string } = {
    'Premium Real Estate Portfolio': 'building-2',
    'Real Estate Portfolio': 'building-2',
    'Clean Energy (LNG)': 'zap',
    'AI Portfolio': 'cpu',
    'Agriculture Fund': 'wheat',
    'Technology Fund': 'smartphone',
    'Healthcare Portfolio': 'heart',
    'Infrastructure Fund': 'construction',
    'default': 'trending-up'
  };
  // Dialog state
  showInvestmentDialog: boolean = false;
  allPortfolios: Portfolio[] = [];
  dialogLoading: boolean = false;

  showPortfolioDialog = false;
  selectedPortfolio: UserInvestment | null = null;
  constructor(private userService: UserService, private http: HttpClient,    private portfolioService: PortfolioService, private tokenIdService: TokenIdService
  ) { }
  

  ngOnInit(): void {
    this.initializeUserId();
    this.loadUserInvestments();
  }

  private initializeUserId(): void {
    try {
      // Check if user is authenticated
      this.isAuthenticated = this.userService.isAuthenticated();
      
      if (!this.isAuthenticated) {
        this.error = 'Please log in to view your investments';
        return;
      }

      // Get the auth token using the service method
      const token = this.userService.getAuthToken();
      
      if (!token) {
        this.error = 'Authentication token not found';
        this.isAuthenticated = false;
        return;
      }

      // Decode token using the static method from UserService
      const decodedToken = this.tokenIdService.decodeToken(token);
      
      if (!decodedToken) {
        this.error = 'Invalid authentication token';
        this.isAuthenticated = false;
        return;
      }

      // Extract userId from decoded token
      // Check multiple possible field names that might contain the user ID
      this.userId = decodedToken.userId 

      if (!this.userId) {
        this.error = 'User ID not found in authentication token';
        this.isAuthenticated = false;
        console.error('Token payload:', decodedToken);
        return;
      }

      console.log('Initialized userId:', this.userId);
      
    } catch (error) {
      console.error('Error initializing user ID:', error);
      this.error = 'Failed to authenticate user';
      this.isAuthenticated = false;
      this.userId = '';
    }
  }

  loadUserInvestments(): void {
    if (!this.userId) {
      this.error = 'User not authenticated';
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.userService.getAllUserInvestments(this.userId).subscribe({
      next: (response: UserInvestmentResponse) => {
        if (response.success) {
          this.userInvestments = response.data;
        } else {
          this.error = 'Failed to load investments';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading user investments:', error);
        this.error = 'Failed to load investments. Please try again.';
        this.isLoading = false;
      }
    });
  }

  getPortfolioIcon(portfolioName: string): string {
    return this.portfolioIcons[portfolioName] || this.portfolioIcons['default'];
  }

  calculateProfitLoss(investment: UserInvestment): number {
    return investment.profitLoss;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }



  getRiskLevelClass(riskLevel: string): string {
    switch (riskLevel.toLowerCase()) {
      case 'low':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'high':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  onViewDetails(investment: UserInvestment): void {
    // Handle view details logic
    console.log('View details for:', investment.portfolioId.name);
    // You can implement navigation to detailed view or open a modal
  }

  onDownloadReport(investment: UserInvestment): void {
    // Handle download report logic
    console.log('Download report for:', investment.portfolioId.name);
    // Implement PDF generation or API call for report download
  }

  showPortfolioDetails(investment: UserInvestment) {
    this.selectedPortfolio = investment;
    this.showPortfolioDialog = true;
  }

  onDialogClose() {
    this.showPortfolioDialog = false;
    this.selectedPortfolio = null;
    this.showInvestmentDialog = false;
  }
//   status: {
//     type: String,
//     enum: ['Upcoming', 'Open', 'Paying', 'Matured'],
//     default: 'Upcoming'
// },
    getStatusClass(status: string): string {
        switch (status) {
            case 'Upcoming':
                return 'status-upcoming';
            case 'Open':
                return 'status-open';
            case 'Paying':
                return 'status-paying';
            default:
                return 'status-matured';
        }
    }

  onAddInvestment(): void {
    this.showInvestmentDialog = true;
    console.log('onAddInvestment',this.showInvestmentDialog)
    this.portfolioService.fetchPortfolios().subscribe({
      next: (res) => {
        if (res.success) {
          this.allPortfolios = res.data;
          console.log(this.allPortfolios,'allPortfolios')
        }
        // this.dialogLoading = false;
      },
      error: (error:any) => {
        console.error('Error fetching portfolios:', error);
        this.dialogLoading = false;
      }
    });
  }



  refreshInvestments(): void {
    this.loadUserInvestments();
  }

  getTotalInvestmentValue(): number {
    return this.userInvestments.reduce((total, investment) => total + investment.totalInvestedAmount, 0);
  }

  getTotalCurrentValue(): number {
    return this.userInvestments.reduce((total, investment) => total + investment.currentValue, 0);
  }

  getTotalProfitLoss(): number {
    return this.userInvestments.reduce((total, investment) => total + investment.profitLoss, 0);
  }

  getAverageROI(): number {
    if (this.userInvestments.length === 0) return 0;
    const totalROI = this.userInvestments.reduce((total, investment) => total + investment.actualRoiPercentage, 0);
    return totalROI / this.userInvestments.length;
  }

  // Split investments by isPublished flag
  get myInvestments(): UserInvestment[] {
    return this.userInvestments.filter(inv => !inv.portfolioId?.isPublished);
  }

  get myPortfolios(): UserInvestment[] {
    return this.userInvestments.filter(inv => inv.portfolioId?.isPublished);
  }

  async downloadPortfolioReport(portfolio: UserInvestment): Promise<void> {
    // Build minimal, clean dataset for PDF
    const data = {
      'Portfolio Name': portfolio.portfolioId.name,
      'Risk Level': portfolio.portfolioId.riskLevel,
      'Status': portfolio.portfolioId.status,
      'Total Invested Amount (AED)': portfolio.totalInvestedAmount,
      'Total Tickets': portfolio.totalTickets,
      'Investment Count': portfolio.investmentCount,
      'Current Value (AED)': portfolio.currentValue,
      'Profit / Loss (AED)': portfolio.profitLoss,
      'Actual ROI (%)': Number(portfolio.actualRoiPercentage?.toFixed(2)),
      'Expected ROI Potential (%)': portfolio.portfolioId.roiPotential,
      'Ticket Price (AED)': portfolio.portfolioId.ticketPrice,
      'Investment Start Date': this.formatDate(portfolio.investmentStartDate),
      'Last Investment Date': this.formatDate(portfolio.lastInvestmentDate),
      'Portfolio Start Date': this.formatDate(portfolio.portfolioId.investmentStartDate),
      'Portfolio Closure Date': this.formatDate(portfolio.portfolioId.investmentClosureDate),
      'Maturity Date': portfolio.portfolioId.maturityDate ? this.formatDate(portfolio.portfolioId.maturityDate) : '—'
    };

    const safeName = portfolio.portfolioId.name.replace(/[^a-z0-9\-_. ]/gi, '_');

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const margin = 14;
      const pageWidth = (doc as any).internal.pageSize.getWidth();

      // Branded header
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.text('HECTOR GLOBAL', pageWidth / 2, 18, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(13);
      doc.text('Portfolio Report', pageWidth / 2, 26, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(90);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 32, { align: 'center' });
      doc.setDrawColor(245, 158, 11);
      doc.setLineWidth(0.5);
      doc.line(margin, 36, pageWidth - margin, 36);

      // Convert key-value to rows
      const body: any[] = Object.entries(data).map(([k, v]) => [k, String(v ?? '')]);

      // Table
      autoTable(doc, {
        startY: 42,
        head: [['Field', 'Value']],
        body,
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [245, 158, 11] }, // amber-ish
        columnStyles: {
          0: { cellWidth: 70, fontStyle: 'bold' },
          1: { cellWidth: 'auto' }
        },
        theme: 'striped'
      });

      doc.save(`${safeName}_Report.pdf`);
    } catch (e) {
      console.error('PDF generation failed:', e);
    }
  }
}
